import api from "api";
import { supportApiBase } from "api/constants";
import { useQuery } from "react-query";
import { CountryCode } from "utils/market";
import { queryKeys } from "utils/misc";

export enum Category {
  q = "q",
  id = "id",
  uid = "uid",
  phone = "phone_number",
  nhs = "nhs_number",
}

export const ALL_ROLES_VALUE = "all";

export const ROLES = [
  { value: ALL_ROLES_VALUE, label: "all" },
  { value: "patient", label: "patients" },
  { value: "therapist", label: "therapists" },
  { value: "support_admin", label: "support admins" },
  { value: "clinic_partner", label: "clinic partners" },
  { value: "hcp_admin", label: "HCP admins" },
  { value: "content_admin", label: "content admins" },
  { value: "billing_admin", label: "billing admins" },
  { value: "hcp_biller", label: "HCP billers" },
] as const;

export type Role = (typeof ROLES)[number]["value"];

export interface Vars {
  category: Category;
  search: string;
  role?: Role;
  country_code?: CountryCode;
  page?: number;
  limit?: number;
}

export interface UserListItem {
  id: number;
  first_name: string | null;
  last_name: string | null;
  preferred_name: string | null;
  email: string | null;
  uid: string;
  roles: string[];
  state: string | null;
  country: string | null;
  phone_number: string | null;
  health_care_provider_name: string | null;
  created_at: string | null;
  deleted_at: string | null;
}

export interface UserListMetadata {
  page: number;
  next: number | null;
  prev: number | null;
  page_items: number;
  total_count: number;
}

export interface UserListResponse {
  metadata: UserListMetadata;
  items: UserListItem[];
}

export const useListUsers = (params: Vars) => {
  const key = queryKeys.userSearch(params);

  return useQuery(
    key,
    async (): Promise<UserListResponse> => {
      const queryParams = new URLSearchParams();
      queryParams.set(params.category, params.search);
      const countryAgnostic = params.category === Category.id || params.category === Category.uid;
      if (params.country_code && !countryAgnostic) queryParams.set("country_code", params.country_code);
      if (params.role && params.role !== ALL_ROLES_VALUE) queryParams.set("role", params.role);
      if (params.page && params.page > 1) queryParams.set("page", String(params.page));
      if (params.limit) queryParams.set("limit", String(params.limit));

      const response = await api.call(`/users?${queryParams}`, { root: supportApiBase });
      return response.json();
    },
    { enabled: !!params.search || (!!params.role && params.role !== ALL_ROLES_VALUE) }
  );
};
